import {createAction, createSlice} from '@reduxjs/toolkit';
import {
  APP_CONTEXT_SLICE_NAME,
  NOTIFICATION_STATUS,
} from '@jobseeker/common-core/constants';
import {HYDRATE} from 'next-redux-wrapper';
import type {AppContextState, AppState} from '@jobseeker/common-core/types';

const initialState: AppContextState = {
  context: {
    navigation: {
      tag: 0,
      selected: 0,
      slug: '',
      location: '',
      actualCategory: 0,
    },
    url: '/',
    reCap: 123,
    isCrawlerBot: false,
    chatCount: 0,
  },
  layout: {
    searchOverlay: false,
    enbaleAdsForPreLoad: false,
    enableChatSticky: false,
    validated: false,
    enableLoginPopup: false,
    device: 'desktop',
    paginationEnabled: false,
    qupOpen: false,
  },
  page: '/',
  notification: {
    count: 0,
    isLoading: null,
    list: [],
  },
  jobdetail: {
    apply: false,
  },
  redirectedFromExpiredJD: false,
  featureFlags: {},
  abVariants: {},
};

const HyDrate = createAction<AppState>(HYDRATE);

const appContext = createSlice({
  name: APP_CONTEXT_SLICE_NAME,
  initialState,
  reducers: {
    set_app_context: (state, action) => {
      state.context = action.payload;
    },
    set_current_page: (state, action) => {
      state.page = action.payload?.page;
    },
    set_user_logged_out: (state, action) => {
      state.notification = initialState.notification;
    },
    set_pagination_enabled: (state, action) => {
      state.layout.paginationEnabled = !!action.payload;
    },
    set_open_searchoverlay: (state, action) => {
      state.layout.searchOverlay = action.payload?.search;
    },
    set_notification_count: (state, action) => {
      state.notification.count = action.payload;
    },
    set_notification_list: (state, action) => {
      state.notification.list = action.payload;
    },
    set_notification_loading: (state, action) => {
      state.notification.isLoading = action.payload;
    },
    set_notification_status: (state, action) => {
      state.notification.list = state.notification.list.map(item => {
        if (item.id === action.payload.id) {
          return {...item, status: NOTIFICATION_STATUS.READ};
        }
        return item;
      });
    },
    set_enable_ads_preload: (state, action) => {
      state.layout.enbaleAdsForPreLoad = action.payload;
    },
    set_navigation_selector: (state, action) => {
      state.context.navigation.selected = action.payload;
    },
    set_tag_selector: (state, action) => {
      state.context.navigation.tag = action.payload;
    },
    set_enable_chat_sticky: (state, action) => {
      state.layout.enableChatSticky = action.payload;
    },
    set_context_url: (state, action) => {
      state.context.url = action.payload;
    },
    set_layout_validated: (state, action) => {
      state.layout.validated = action.payload;
    },
    set_recap: (state, action) => {
      state.context.reCap = action.payload;
    },
    set_jd_apply: (state, action) => {
      state.jobdetail.apply = action.payload;
    },
    set_layout_device: (state, action) => {
      state.layout.device = action.payload;
    },
    set_bot: (state, action) => {
      state.context.isCrawlerBot = action.payload;
    },
    set_slug: (state, action) => {
      state.context.navigation.slug = action.payload;
    },
    set_slug_url: (state, action) => {
      state.context.navigation.slugUrl = action.payload;
    },
    set_naviagtion_location: (state, action) => {
      state.context.navigation.location = action.payload;
    },
    set_login_popup: (state, action) => {
      state.layout.enableLoginPopup = action.payload;
    },
    set_qup_open: (state, action) => {
      state.layout.qupOpen = !!action.payload;
    },
    set_chat_count: (state, action) => {
      state.context.chatCount = action.payload;
    },
    set_redirectedFromExpiredJD: (state, action) => {
      state.redirectedFromExpiredJD = action.payload as boolean;
    },
    set_category_actual: (state, action) => {
      state.context.navigation.actualCategory = action.payload;
    },
    set_feature_flags: (state, action) => {
      state.featureFlags = action.payload;
    },
    set_ab_variants: (state, action) => {
      state.abVariants = {...state.abVariants, ...action.payload};
    },
  },
  extraReducers(builder) {
    builder.addCase(HyDrate, (state, action) => {
      if (!action.payload?.app) {
        return state;
      }

      state.page = action.payload?.app?.page;
      state.layout.enbaleAdsForPreLoad =
        action.payload?.app?.layout?.enbaleAdsForPreLoad;
      state.context.navigation.selected =
        action.payload?.app?.context?.navigation?.selected;
      state.context.navigation.tag =
        action.payload?.app?.context?.navigation?.tag;
      state.context.url = action.payload?.app?.context?.url;
      state.context.isCrawlerBot = action.payload?.app?.context?.isCrawlerBot;
      state.jobdetail.apply = action.payload?.app?.jobdetail?.apply;
      state.layout.device = action.payload?.app?.layout?.device;
      state.context.navigation.slug =
        action.payload?.app?.context?.navigation?.slug;
      state.context.navigation.slugUrl =
        action.payload?.app?.context?.navigation?.slugUrl;
      state.context.navigation.location =
        action.payload?.app?.context?.navigation?.location;
      state.redirectedFromExpiredJD =
        action.payload?.app?.redirectedFromExpiredJD || false;
      state.context.navigation.actualCategory =
        action.payload?.app?.context?.navigation?.actualCategory;
      state.featureFlags = action.payload?.app?.featureFlags || {};
      const incomingAbVariants = action.payload?.app?.abVariants;
      state.abVariants =
        incomingAbVariants && Object.keys(incomingAbVariants).length > 0
          ? incomingAbVariants
          : state.abVariants;
    });
  },
});

export const {
  set_app_context,
  set_current_page,
  set_user_logged_out,
  set_open_searchoverlay,
  set_notification_count,
  set_notification_list,
  set_notification_status,
  set_notification_loading,
  set_enable_ads_preload,
  set_navigation_selector,
  set_redirectedFromExpiredJD,
  set_tag_selector,
  set_feature_flags,
  set_ab_variants,
} = appContext.actions;

const reducer = appContext.reducer;

export {reducer, initialState};
